import { api } from '@goaltree/backend/convex/_generated/api'
import { useAction, useMutation, useQuery } from 'convex/react'
import { Camera, Mail, PlusIcon, RefreshCwIcon, XIcon } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

const PROVIDER_LABEL: Record<string, string> = {
  gmail: 'Gmail',
  instagram: 'Instagram',
}

const PROVIDER_ICON: Record<string, React.ReactElement> = {
  gmail: <Mail className="h-4 w-4" />,
  instagram: <Camera className="h-4 w-4" />,
}

const PROVIDERS = ['gmail', 'instagram'] as const

export function AppConnectionsSettings() {
  const { t } = useTranslation()
  const connections = useQuery(api.apps.getConnectionStatus)
  const available = useQuery(api.apps.available)
  const getAuthUrl = useAction(api.apps.getAuthUrl)
  const disconnect = useMutation(api.apps.disconnect).withOptimisticUpdate(
    (store, { provider }) => {
      const cur = store.getQuery(api.apps.getConnectionStatus, {})
      if (cur)
        store.setQuery(
          api.apps.getConnectionStatus,
          {},
          cur.filter((c) => c.provider !== provider),
        )
    },
  )

  async function handleConnect(provider: 'gmail' | 'instagram') {
    try {
      const { webUrl } = await getAuthUrl({ provider })
      window.location.href = webUrl
    } catch {
      toast.error(t('settings.apps.connectError', { app: PROVIDER_LABEL[provider] }))
    }
  }

  async function handleDisconnect(provider: 'gmail' | 'instagram') {
    try {
      await disconnect({ provider })
      toast.success(t('settings.apps.disconnected', { app: PROVIDER_LABEL[provider] }))
    } catch {
      toast.error(t('settings.apps.disconnectError', { app: PROVIDER_LABEL[provider] }))
    }
  }

  if (connections === undefined || available === undefined) {
    return (
      <div className="flex items-center gap-4 px-4 py-3.5">
        <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
          <div className="h-4 w-4 rounded bg-muted-foreground/20 animate-pulse" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="h-4 w-40 rounded bg-muted animate-pulse" />
          <div className="mt-1.5 h-3 w-56 max-w-full rounded bg-muted/60 animate-pulse" />
        </div>
        <div className="h-7 w-20 shrink-0 rounded-lg bg-muted animate-pulse" />
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {PROVIDERS.filter((p) => available[p]).map((provider) => {
        const conn = connections.find((c) => c.provider === provider)
        const needsReauth =
          conn?.status === 'requires_reauth' ||
          (provider === 'instagram' && !!conn?.expiresAt && conn.expiresAt < Date.now())
        return (
          <div key={provider} className="flex items-center gap-4 px-4 py-3">
            <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              {needsReauth ? (
                <RefreshCwIcon className="h-4 w-4 text-warning" />
              ) : (
                PROVIDER_ICON[provider]
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {conn
                  ? needsReauth
                    ? t('settings.apps.requiresReauth', { app: PROVIDER_LABEL[provider] })
                    : t('settings.apps.connectedTitle')
                  : PROVIDER_LABEL[provider]}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t('settings.apps.description', { app: PROVIDER_LABEL[provider] })}
              </p>
              {conn && !needsReauth && (
                <p className="mt-0.5 text-xs text-muted-foreground/60 truncate">
                  {conn.externalUserId}
                </p>
              )}
              {conn && !needsReauth && !conn.canPublish && (
                <p className="mt-1 text-xs text-warning">{t('settings.apps.instagramPersonal')}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {conn && needsReauth && (
                <button
                  type="button"
                  onClick={() => void handleConnect(provider)}
                  className="cursor-pointer rounded-lg bg-warning/15 px-3 py-1.5 text-xs font-medium text-warning transition-colors hover:bg-warning/25"
                >
                  {t('settings.apps.reconnect')}
                </button>
              )}
              {!conn && (
                <button
                  type="button"
                  onClick={() => void handleConnect(provider)}
                  className="cursor-pointer shrink-0 flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <PlusIcon className="h-3 w-3" strokeWidth={2.5} />
                  {t('settings.apps.connect')}
                </button>
              )}
              {conn && !needsReauth && (
                <button
                  type="button"
                  onClick={() => void handleDisconnect(provider)}
                  className="cursor-pointer text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                  title={t('settings.apps.disconnect')}
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
